import type { MultiSelectObjectOption } from '@collector/shared-ui'
import {
  type EventDetailId,
  type SportDetailPossibleValue,
} from '@collector/sportsapi-client-legacy'

export type SelectOptionDisableRule = {
  detailId: EventDetailId
  /** Option value, or key, that cannot be selected while `when` is true. */
  option: string
  when: (
    currentValue: string | number | null,
    possibleValues: SportDetailPossibleValue[],
  ) => boolean
}

function represents(
  possibleValue: SportDetailPossibleValue,
  expected: string | number,
): boolean {
  return (
    possibleValue.value === expected ||
    String(possibleValue.key) === String(expected)
  )
}

/** Rule condition: the detail's current value is `expected`, by value or key. */
export function whenCurrentValueIs(
  expected: string | number,
): SelectOptionDisableRule['when'] {
  return (currentValue, possibleValues) => {
    if (currentValue === expected) {
      return true
    }

    const match = possibleValues.find((possibleValue) =>
      represents(possibleValue, expected),
    )

    return (
      match !== undefined &&
      (currentValue === match.value || currentValue === match.key)
    )
  }
}

export function isSelectOptionDisabled(
  rules: SelectOptionDisableRule[],
  detailId: EventDetailId,
  currentValue: string | number | null,
  option: SportDetailPossibleValue,
  possibleValues: SportDetailPossibleValue[],
): boolean {
  return rules.some(
    (rule) =>
      detailId === rule.detailId &&
      represents(option, rule.option) &&
      rule.when(currentValue, possibleValues),
  )
}

export function getEventDetailSelectOptions(
  detailId: EventDetailId,
  currentValue: string | number | null,
  possibleValues: SportDetailPossibleValue[],
  rules: SelectOptionDisableRule[],
): MultiSelectObjectOption[] {
  return possibleValues.map((possibleValue) => ({
    name: possibleValue.value,
    value: possibleValue.value,
    disabled: isSelectOptionDisabled(
      rules,
      detailId,
      currentValue,
      possibleValue,
      possibleValues,
    ),
  }))
}
